import * as React from 'react';
import styled from 'styled-components';
import { PaginationOptions } from '.';
import { Flex, FlexGrow } from '../Flex';

const defaultComponentOptions = {
  rowsPerPageText: 'Records per page:',
  rangeSeparatorText: 'of',
  noRowsPerPage: false,
  selectAllRowsItem: false,
  selectAllRowsItemText: 'All',
};

const PaginationWrapper = styled(Flex)`${({ theme }) => `
    align-items: center;  
    padding:calc(${theme.padding[4]} - ${theme.borderWidth[1]}) ${theme.padding[6]};
`}`;

const Button = styled.button`
    border: none;
    background:none;
`;

const PageButton = styled.button<{current: boolean}>`${({ theme, current }) => `
  background: ${theme.colours.neutral[10]};
  color: ${current ? theme.colours.primary[1] : theme.colours.neutral[2]};
  font-weight:${current ? theme.fontWeight.bolder : theme.fontWeight.normal};
  border:none;
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[2]};
  border-bottom: ${current && `${theme.borderWidth[2]} solid`};
  width:${theme.padding[8]};
  height:${theme.padding[8]};
  &:hover{
    font-weight:${theme.fontWeight.bolder}
  }
`}`;

const PageList = styled(Flex)`${({ theme }) => `
    align-items: center;
    gap: calc(${theme.borderWidth[2]} + ${theme.borderWidth[3]});
`}`;

const Span = styled.span`
    flex-shrink: 1;
    user-select: none;
`;

const RowLabel = styled(Span)`${({ theme }) => `
  color:${theme.colours.neutral[3]};
  font-family: 'Open Sans';
  font-weight: ${theme.fontWeight.normal};
  font-size:  ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]}; 
`}`;

const Icon = styled.i`${({ theme }) => `
  color:${theme.colours.primary[1]};
  font-weight:${theme.fontWeight.bold};
  font-size: ${theme.fontSize[3]};
  padding: ${theme.padding[2]};
`}`;

const Select = styled.select`${({ theme }) => `
  color:${theme.colours.neutral[2]};
  font-weight:${theme.fontWeight.bold};
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[2]};
  border: none;
  line-height: ${theme.fontSize[6]}; 
  outline:none;
`}`;

interface PaginationProps {
    rowsPerPage: number,
    rowCount: number,
    currentPage: number,
    onChangePage: (page: number) => void,
    onChangeRowsPerPage: (value: number, currentPage: number) => void,
    paginationComponentOptions: PaginationOptions,
}

const getNumberOfPages = (rowCount: number, rowsPerPage: number) => Math.ceil(rowCount / rowsPerPage);

// eslint-disable-next-line react/function-component-definition
export const TablePagination = ({
  rowsPerPage,
  rowCount,
  currentPage,
  onChangePage = () => {},
  onChangeRowsPerPage = () => {},
  paginationComponentOptions,
}: PaginationProps) => {
  const numPages = getNumberOfPages(rowCount, rowsPerPage);
  const disabledLesser = currentPage === 1;
  const disabledGreater = currentPage === numPages;
  const options = { ...defaultComponentOptions, ...paginationComponentOptions };

  const handlePrevious = React.useCallback(() => onChangePage(currentPage - 1), [currentPage, onChangePage]);
  const handleNext = React.useCallback(() => onChangePage(currentPage + 1), [currentPage, onChangePage]);
  const handleFirst = React.useCallback(() => onChangePage(1), [onChangePage]);
  const handleLast = React.useCallback(
    () => onChangePage(getNumberOfPages(rowCount, rowsPerPage)),
    [onChangePage, rowCount, rowsPerPage],
  );

  const handleRowsPerPage = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChangeRowsPerPage(Number(e.target.value), currentPage),
    [currentPage, onChangeRowsPerPage],
  );

  const selectOptions = [10, 20, 30, 40, 50].map((num: number) => (
    <option key={num} value={num}>
      {num}
    </option>
  ));

  if (options.selectAllRowsItem) {
    selectOptions.push(
      <option key={-1} value={rowCount}>
        {options.selectAllRowsItemText}
      </option>,
    );
  }

  const select = (
    <Select onChange={handleRowsPerPage} defaultValue={rowsPerPage} aria-label={options.rowsPerPageText}>
      {selectOptions}
    </Select>
  );

  const showPageButton = (pageNumber: number) => numPages < 6
    || pageNumber === 1
    || pageNumber === currentPage
    || pageNumber === currentPage - 1
    || pageNumber === currentPage - 2
    || pageNumber === currentPage + 1
    || pageNumber === currentPage + 2
    || pageNumber === numPages;

  const isEllipsis = (pageNumber: number) => showPageButton(pageNumber) === false
    && (
      pageNumber === currentPage - 3
      || pageNumber === currentPage + 3
    );

  const pageButtons = [...Array(numPages)].map((_x, i) => ({
    show: showPageButton(i + 1),
    number: i + 1,
    ellipsis: isEllipsis(i + 1),
  }));

  return (
    <PaginationWrapper>
      <FlexGrow>
        {!options.noRowsPerPage && (
        <>
          <RowLabel>{options.rowsPerPageText}</RowLabel>
          {select}
        </>
        )}
      </FlexGrow>

      <PageList>
        {currentPage !== 1 && (
          <>
            <Button
              id="pagination-first-page"
              type="button"
              aria-label="First Page"
              aria-disabled={disabledLesser}
              onClick={handleFirst}
              disabled={disabledLesser}
            >
              <Icon className="mi-chevron-double-left" />
            </Button>

            <Button
              id="pagination-previous-page"
              type="button"
              aria-label="Previous Page"
              aria-disabled={disabledLesser}
              onClick={handlePrevious}
              disabled={disabledLesser}
            >
              <Icon className="mi-chevron-left" />
            </Button>
          </>
        )}

        {pageButtons.map(({ show, number, ellipsis }) => (
          <React.Fragment key={number}>
            {ellipsis && (
              <p>...</p>
            )}
            {show && (
              <PageButton
                onClick={() => onChangePage(number)}
                current={number === currentPage}
              >
                {number}
              </PageButton>
            )}
          </React.Fragment>
        ))}

        {currentPage < numPages && (
          <>
            <Button
              id="pagination-next-page"
              type="button"
              aria-label="Next Page"
              aria-disabled={disabledGreater}
              onClick={handleNext}
              disabled={disabledGreater}
            >
              <Icon className="mi-chevron-right" />
            </Button>

            <Button
              id="pagination-last-page"
              type="button"
              aria-label="Last Page"
              aria-disabled={disabledGreater}
              onClick={handleLast}
              disabled={disabledGreater}
            >
              <Icon className="mi-chevron-double-right" />
            </Button>
          </>
        )}
      </PageList>
    </PaginationWrapper>
  );
};
